import {
  setCurrentPageName as setCurrentPageNameInGlobalTelemetry,
  setPageLoadNumber as setPageLoadNumberInGlobalTelemetry,
} from '@ms/yammer-telemetry-support';
import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { emitPageLoadEvent } from '../../telemetry/loadEvents';
import { getPageLoadId, incrementPageLoadId } from '../../telemetry/pageLoadId';

import type { PageTelemetryContextProviderProps } from './PageTelemetryContextProvider.types';
import type { PageTelemetryContextValue, RouteProps } from './context';
import { PageTelemetryContext } from './context';
import { useReportHeroComponentRenderedCallback, useSetFirstLoadedPageSubscription } from './hooks';

interface ContextState extends RouteProps {
  readonly pathname?: string;
  readonly pageLoadId: number;
  readonly hasHeroComponentRendered: boolean;
}

const PageTelemetryContextProvider: FC<PropsWithChildren<PageTelemetryContextProviderProps>> = ({
  isRoutelessApp = false,
  children,
}) => {
  useSetFirstLoadedPageSubscription();
  const hasRenderedFirstRoute = useRef(false);

  const [routeProps, setRouteProps] = useState<ContextState>({
    pathname: undefined,
    pageLoadId: getPageLoadId(),
    path: undefined,
    page: 'Unknown',
    // eslint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    hasHeroComponentRendered: false,
  });

  const setHeroComponentRendered = useCallback(() => {
    // istanbul ignore else
    if (!routeProps.hasHeroComponentRendered) {
      setRouteProps({
        pathname: routeProps.pathname,
        pageLoadId: routeProps.pageLoadId,
        path: routeProps.path,
        page: routeProps.page,
        hero: routeProps.hero,
        hasHeroComponentRendered: true,
      });
    }
  }, [
    routeProps.hasHeroComponentRendered,
    routeProps.hero,
    routeProps.page,
    routeProps.pageLoadId,
    routeProps.path,
    routeProps.pathname,
  ]);

  const setRoute = useCallback(
    (props: RouteProps, pathname: string) => {
      const hasRouteChanged =
        props.path !== routeProps.path ||
        props.page !== routeProps.page ||
        props.hero !== routeProps.hero ||
        pathname !== routeProps.pathname;

      if (!hasRenderedFirstRoute.current) {
        hasRenderedFirstRoute.current = true;
      } else if (hasRouteChanged) {
        incrementPageLoadId();
        emitPageLoadEvent('RouteChanged');
      }

      const pageLoadId = getPageLoadId();
      const hasPageLoadIdChanged = pageLoadId !== routeProps.pageLoadId;
      const hasHeroComponentRendered = hasRouteChanged ? false : routeProps.hasHeroComponentRendered;
      if (hasRouteChanged || hasPageLoadIdChanged) {
        setRouteProps({
          ...props,
          pathname,
          pageLoadId,
          hasHeroComponentRendered,
        });
      }
    },
    [
      routeProps.hasHeroComponentRendered,
      routeProps.hero,
      routeProps.page,
      routeProps.pageLoadId,
      routeProps.path,
      routeProps.pathname,
    ]
  );

  setCurrentPageNameInGlobalTelemetry(routeProps.page);
  setPageLoadNumberInGlobalTelemetry(routeProps.pageLoadId);

  const reportHeroComponentRendered = useReportHeroComponentRenderedCallback({
    ...routeProps,
    isRoutelessApp,
    onHeroComponentRendered: setHeroComponentRendered,
  });

  const value = useMemo<PageTelemetryContextValue>(
    () => ({
      page: routeProps.page,
      path: routeProps.path,
      hero: routeProps.hero,
      pathname: routeProps.pathname,
      isRoutelessApp,
      setRoute,
      reportHeroComponentRendered,
      hasHeroComponentRendered: routeProps.hasHeroComponentRendered,
    }),
    [
      routeProps.page,
      routeProps.path,
      routeProps.hero,
      routeProps.pathname,
      isRoutelessApp,
      setRoute,
      reportHeroComponentRendered,
      routeProps.hasHeroComponentRendered,
    ]
  );

  return <PageTelemetryContext.Provider value={value}>{children}</PageTelemetryContext.Provider>;
};

export default PageTelemetryContextProvider;
